import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button, Card, Badge, Spinner, fmtBRL, fmtKg, fmtNum } from "@/components/ui/Primitives";
import { api, formatErr, fmtDate } from "@/lib/api";
import { ArrowLeft, Play, Tractor, Package, Trash2, Truck, Weight, DollarSign, Calendar, MapPin, FileText, Printer, Users, RotateCcw, Pencil } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const CATS = [
  { k: "cx1", label: "CX1", subtitle: "Refugo", color: "bg-red-100 text-red-800 border-red-200" },
  { k: "cx2", label: "CX2", subtitle: "Pequena", color: "bg-orange-100 text-orange-800 border-orange-200" },
  { k: "cx3", label: "CX3", subtitle: "Média", color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { k: "cx3g", label: "CX3G", subtitle: "Grande", color: "bg-sky-100 text-sky-800 border-sky-200" },
  { k: "cx4", label: "CX4", subtitle: "Extra", color: "bg-purple-100 text-purple-800 border-purple-200" },
];

export default function InvoiceDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [inv, setInv] = useState(null);
  const [pallets, setPallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: invData }, { data: palletData }] = await Promise.all([
        api.get(`/invoices/${id}`),
        api.get(`/invoices/${id}/pallets`),
      ]);
      setInv(invData);
      setPallets(palletData || []);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const iniciar = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/invoices/${id}/iniciar-beneficiamento`);
      nav(`/invoices/${id}/beneficiamento`);
    } catch (e) { setErr(formatErr(e)); setBusy(false); }
  };

  const onDelete = async () => {
    if (!window.confirm(`Excluir NF #${inv?.nota_numero}? Esta ação não pode ser desfeita.`)) return;
    try { await api.delete(`/invoices/${id}`); nav("/invoices"); }
    catch (e) { alert(formatErr(e)); }
  };

  const reabrir = async () => {
    if (!window.confirm(
      `Reabrir NF #${inv?.nota_numero} para edição de pallets?\n\n` +
      `• Status voltará a "em beneficiamento"\n` +
      `• Você poderá editar/excluir/adicionar pallets\n` +
      (inv?.tipo_operacao === "terceiros"
        ? `• A Conta a Receber existente será REMOVIDA — nova será gerada ao finalizar novamente\n`
        : `• O Contas a Pagar continuará existindo (será atualizado ao re-finalizar)\n`) +
      `\nDeseja continuar?`
    )) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/invoices/${id}/reabrir`);
      nav(`/invoices/${id}/beneficiamento`);
    } catch (e) { setErr(formatErr(e)); setBusy(false); }
  };

  if (loading) return <Layout title="Carregando..."><div className="flex justify-center py-20"><Spinner size={28} /></div></Layout>;
  if (!inv) return <Layout title="NF não encontrada"><Card className="p-6">Nota Fiscal não encontrada.</Card></Layout>;

  const statusLabel = inv.status === "bruto" ? "Pendente" : inv.status === "em_beneficiamento" ? "Em beneficiamento" : "Finalizada";
  const totalPallets = pallets.length;
  const totalCaixas = pallets.reduce((a, p) => a + (Number(p.total_caixas) || 0), 0);
  const isTerc = inv.tipo_operacao === "terceiros";

  return (
    <Layout
      title={`NF #${inv.nota_numero}${isTerc ? " • Terceiros" : ""}`}
      subtitle={isTerc ? `${inv.cliente_dono} • ${inv.tipo_cebola}` : `${inv.fornecedor} • ${inv.tipo_cebola}`}
      actions={
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => nav(isTerc ? "/terceiros/nfs" : "/invoices")} data-testid="back-to-list">
            <ArrowLeft size={16} /> Voltar
          </Button>
          <Button variant="secondary" onClick={() => nav(`/invoices/${id}/edit`)} data-testid="edit-invoice-btn">
            <Pencil size={16} /> Editar
          </Button>
          {inv.status === "bruto" && (
            <Button onClick={iniciar} disabled={busy} data-testid="start-beneficiamento-btn">
              {busy ? <Spinner size={16} /> : <><Play size={16} /> Iniciar Beneficiamento</>}
            </Button>
          )}
          {inv.status === "em_beneficiamento" && (
            <Link to={`/invoices/${id}/beneficiamento`}>
              <Button data-testid="continue-beneficiamento-btn"><Tractor size={16} /> Continuar Beneficiamento</Button>
            </Link>
          )}
          {inv.status === "beneficiada" && isAdmin && (
            <Button
              onClick={reabrir}
              disabled={busy}
              variant="secondary"
              className="!bg-amber-50 !text-amber-800 border-amber-300 hover:!bg-amber-100"
              data-testid="reabrir-invoice-btn"
              title="Reabrir NF para edição dos pallets (admin apenas)"
            >
              {busy ? <Spinner size={16} /> : <><RotateCcw size={16} /> Reabrir Entrada</>}
            </Button>
          )}
          {inv.status === "beneficiada" && isTerc && (
            <Link to={`/terceiros/nfs/${id}/romaneio`} target="_blank">
              <Button className="!bg-purple-600 hover:!bg-purple-700" data-testid="print-romaneio-terceiros"><Printer size={16} /> Romaneio Terceiros</Button>
            </Link>
          )}
          {inv.status === "beneficiada" && !isTerc && (
            <>
              <Link to={`/invoices/${id}/romaneio`} target="_blank">
                <Button variant="secondary" data-testid="print-romaneio-link"><Printer size={16} /> Romaneio Produtor</Button>
              </Link>
              <Link to={`/invoices/${id}/romaneio-custos`} target="_blank">
                <Button variant="secondary" data-testid="print-romaneio-custos-link" className="!bg-amber-50 !text-amber-800 border-amber-300"><Printer size={16} /> Romaneio Custos</Button>
              </Link>
              <Link to={`/admin/benef-precos?nf=${id}`}>
                <Button data-testid="set-prices-btn"><DollarSign size={16} /> Definir Preços</Button>
              </Link>
            </>
          )}
        </div>
      }
    >
      {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md mb-4">{err}</div>}

      {isTerc && (
        <div className="mb-4 p-4 rounded-lg bg-purple-50 border-l-4 border-purple-500 flex items-start gap-3" data-testid="terceiros-banner">
          <Users size={20} className="text-purple-700 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-bold text-purple-900">Beneficiamento a Terceiros</div>
            <div className="text-sm text-purple-800 mt-0.5">
              Cliente: <strong>{inv.cliente_dono}</strong>{inv.cliente_dono_cnpj ? ` (CNPJ ${inv.cliente_dono_cnpj})` : ""}
            </div>
            <div className="text-xs text-purple-700 mt-1">
              Serviço: <span className="font-mono font-bold">R$ {Number(inv.preco_servico_sc || 0).toFixed(2)}/saco</span>
              {" • "}Sacaria: <strong>{inv.usa_sacaria_propria ? "própria (Serenata)" : "do cliente"}</strong>
              {inv.total_caixas > 0 && (
                <>{" • "}Total: <span className="font-mono font-bold">R$ {(Number(inv.total_caixas || 0) * Number(inv.preco_servico_sc || 0)).toFixed(2)}</span></>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header status */}
      <Card className="p-5 mb-5 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center">
            <Tractor size={28} className="text-brand-500" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Status</div>
            <div className="flex items-center gap-2 mt-0.5">
              <Badge tone={inv.status} className="text-sm">{statusLabel}</Badge>
              {inv.data_beneficiamento && (
                <span className="text-xs text-ink-500 font-mono">
                  Finalizada em {fmtDate(inv.data_beneficiamento)}
                </span>
              )}
            </div>
          </div>
        </div>
        <button onClick={onDelete} className="px-3 py-2 rounded-md text-sm text-danger hover:bg-red-50 border border-red-200 flex items-center gap-2" data-testid="delete-invoice-btn">
          <Trash2 size={16} /> Excluir NF
        </button>
      </Card>

      {/* NF Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        <Card className="p-5">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-3 flex items-center gap-1.5">
            <FileText size={12} /> Nota Fiscal
          </div>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-ink-500">Número</dt><dd className="font-mono font-semibold">#{inv.nota_numero}</dd></div>
            <div className="flex justify-between"><dt className="text-ink-500">Romaneio</dt><dd className="font-mono">RM-{String(inv.romaneio_numero || 0).padStart(4, "0")}</dd></div>
            <div className="flex justify-between"><dt className="text-ink-500">{isTerc ? "Cliente (dono)" : "Fornecedor"}</dt><dd className="font-medium text-ink-900 text-right">{isTerc ? inv.cliente_dono : inv.fornecedor}</dd></div>
            <div className="flex justify-between"><dt className="text-ink-500">Tipo</dt><dd className="capitalize">{inv.tipo_cebola}</dd></div>
            <div className="flex justify-between"><dt className="text-ink-500">Unidade</dt><dd className="font-medium">{inv.unidade || "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-ink-500">Data entrada</dt><dd className="font-mono">{fmtDate(inv.data_entrada)}</dd></div>
          </dl>
        </Card>

        <Card className="p-5">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-3 flex items-center gap-1.5">
            <Weight size={12} /> Pesagem
          </div>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-ink-500">Peso bruto</dt><dd className="font-mono">{fmtKg(inv.peso_bruto)}</dd></div>
            <div className="flex justify-between"><dt className="text-ink-500">Peso tara</dt><dd className="font-mono">{fmtKg(inv.peso_tara)}</dd></div>
            <div className="flex justify-between font-semibold"><dt>Peso líquido</dt><dd className="font-mono text-brand-500">{fmtKg(inv.peso_liquido)}</dd></div>
            {inv.status === "beneficiada" && (
              <>
                <div className="flex justify-between"><dt className="text-ink-500">Total sacos</dt><dd className="font-mono">{fmtNum(inv.total_caixas, 0)}</dd></div>
                <div className="flex justify-between"><dt className="text-ink-500">Quebra</dt><dd className="font-mono text-danger">{fmtKg(inv.quebra_kg)}</dd></div>
              </>
            )}
          </dl>
        </Card>

        <Card className="p-5">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-3 flex items-center gap-1.5">
            <Truck size={12} /> Transporte
          </div>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-ink-500">Motorista</dt><dd>{inv.motorista || "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-ink-500">Placa</dt><dd className="font-mono">{inv.placa || "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-ink-500"><MapPin size={12} className="inline" /> Origem</dt><dd className="text-right">{inv.origem || "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-ink-500"><DollarSign size={12} className="inline" /> Valor NF</dt><dd className="font-mono">{fmtBRL(inv.valor_nf)}</dd></div>
            {inv.vencimento_fornecedor && (
              <div className="flex justify-between"><dt className="text-ink-500"><Calendar size={12} className="inline" /> Vencimento</dt><dd className="font-mono">{inv.vencimento_fornecedor}</dd></div>
            )}
          </dl>
        </Card>
      </div>

      {/* Beneficiamento (só se beneficiada) */}
      {inv.status === "beneficiada" && (
        <Card className="p-5 mb-5">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-4">Beneficiamento — Resumo</div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {CATS.map((c) => {
              const b = (inv.beneficiamento || {})[c.k] || { quantidade: 0, preco: 0 };
              return (
                <div key={c.k} className={`border rounded-lg p-3 ${c.color}`}>
                  <div className="font-bold text-lg">{c.label}</div>
                  <div className="text-xs opacity-70">{c.subtitle}</div>
                  <div className="mt-2 text-2xl font-mono font-bold">{fmtNum(b.quantidade, 0)}</div>
                  <div className="text-xs opacity-70">sacos</div>
                  {!isTerc && b.preco > 0 && <div className="text-xs mt-1 font-mono">{fmtBRL(b.preco)}/saco</div>}
                </div>
              );
            })}
          </div>
          {isTerc && inv.total_caixas > 0 && (
            <div className="mt-4 p-3 rounded-md bg-purple-50 border border-purple-200 flex items-center justify-between">
              <div className="text-sm text-purple-900">
                <strong>{fmtNum(inv.total_caixas, 0)} sacos</strong> beneficiados × R$ {Number(inv.preco_servico_sc || 0).toFixed(2)}/sc
              </div>
              <div className="font-mono font-bold text-purple-800">
                Total serviço: {fmtBRL(Number(inv.total_caixas || 0) * Number(inv.preco_servico_sc || 0))}
              </div>
            </div>
          )}
          {!isTerc && (inv.valor_total_caixas > 0) && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div className="p-3 rounded-md bg-stone-50 border border-ink-200">
                <div className="text-xs text-ink-500">Valor Bruto</div>
                <div className="font-mono font-semibold">{fmtBRL(inv.valor_total_caixas)}</div>
              </div>
              <div className="p-3 rounded-md bg-stone-50 border border-ink-200">
                <div className="text-xs text-ink-500">Funrural</div>
                <div className="font-mono text-danger">-{fmtBRL(inv.funrural)}</div>
              </div>
              <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200">
                <div className="text-xs text-emerald-700 font-bold">A Pagar Produtor</div>
                <div className="font-mono font-bold text-emerald-700">{fmtBRL(inv.valor_liquido_fornecedor)}</div>
              </div>
              <div className="p-3 rounded-md bg-stone-50 border border-ink-200">
                <div className="text-xs text-ink-500">Custos Empresa</div>
                <div className="font-mono text-ink-700">{fmtBRL(inv.custos_operacionais || 0)}</div>
                <div className="text-[10px] text-ink-500 mt-0.5">maquinação + sacaria + frete + chapa</div>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Pallets */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Pallets criados</div>
            <div className="text-sm text-ink-900 mt-0.5">
              {totalPallets} pallets • {fmtNum(totalCaixas, 0)} sacos
            </div>
          </div>
        </div>
        {pallets.length === 0 ? (
          <div className="text-sm text-ink-500 py-6 text-center">Nenhum pallet criado ainda.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {pallets.map((p) => {
              const it = (p.itens || [])[0] || {};
              const cat = CATS.find((c) => c.k === it.categoria);
              return (
                <Link key={p.id} to={`/pallet-label/${p.id}`} className={`border rounded-lg p-3 hover:shadow-md transition-shadow ${cat?.color || "bg-stone-50"}`} data-testid={`pallet-card-${p.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-mono font-bold">{p.numero}</div>
                    <Package size={16} className="opacity-60" />
                  </div>
                  <div className="text-xs opacity-80 mt-1">{cat?.label} • {cat?.subtitle}</div>
                  <div className="text-lg font-bold mt-1">{fmtNum(it.quantidade, 0)} sacos</div>
                </Link>
              );
            })}
          </div>
        )}
      </Card>

      {inv.observacoes && (
        <Card className="p-5 mt-5">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-2">Observações</div>
          <div className="text-sm text-ink-700 whitespace-pre-wrap">{inv.observacoes}</div>
        </Card>
      )}
    </Layout>
  );
}
